export const reviewed = "2026-09-27"
export const studyTypes = ["Overview", "Principles", "Systems"]
export const studyOverview = {
  title: "How to Study Effectively: 8 Principles and the Systems That Use Them",
  description:
    "Choose a study approach for remembering, understanding and solving problems. Explore eight learning principles, eight practical systems and an example weekly plan.",
  url: "/study-skills/",
}
export const sources = {
  review: {
    title: "Dunlosky et al. (2013): Improving Students’ Learning",
    url: "https://journals.sagepub.com/stoken/rbtfl/Z10jaVH/60XQM/full",
  },
  retrieval: {
    title:
      "Agarwal et al. (2021): Retrieval Practice in Schools and Classrooms",
    url: "https://doi.org/10.1007/s10648-021-09595-9",
  },
  ies: {
    title: "IES: Organizing Instruction and Study to Improve Student Learning",
    url: "https://ies.ed.gov/ncee/wwc/PracticeGuide/1",
  },
  feedback: {
    title: "Wisniewski et al. (2020): The Power of Feedback Revisited",
    url:
      "https://www.frontiersin.org/journals/psychology/articles/10.3389/fpsyg.2019.03087/full",
  },
  regulation: {
    title: "EEF: Metacognition and Self-Regulation",
    url:
      "https://educationendowmentfoundation.org.uk/education-evidence/teaching-learning-toolkit/metacognition-and-self-regulation",
  },
  cornell: {
    title: "Cornell University: The Cornell Note Taking System",
    url:
      "https://lsc.cornell.edu/how-to-study/taking-notes/cornell-note-taking-system/",
  },
  reading: {
    title: "Cornell University: Textbook Reading Systems",
    url:
      "https://lsc.cornell.edu/how-to-study/reading-strategies/textbook-reading-systems/",
  },
  leitner: {
    title: "Kansas State University: Leitner System",
    url:
      "https://www.k-state.edu/aac/success-tools/collection/leitner_system.html",
  },
  pomodoro: {
    title: "Francesco Cirillo: The Pomodoro Technique",
    url: "https://www.pomodorotechnique.com/",
  },
  breaks: {
    title: "Smits et al. (2025): Comparing Study Break Techniques",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12292963/",
  },
}
export interface StudySkill {
  id: string
  kind: "Principles" | "Systems"
  slug: string
  title: string
  description: string
  intro: string
  evidence: string
  sourceIds: (keyof typeof sources)[]
  methods: { title: string; text: string }[]
  pattern: string[]
  example: { title: string; paragraphs: string[] }
  transfer: string
  pitfalls: string[]
  week: { day: string; task: string; minutes: number }[]
  subject: string
  principleIds: string[]
  optionalPrincipleIds?: string[]
  setup?: string
  effort?: string
  editorial?: boolean
}
export const principles: StudySkill[] = [
  {
    id: "retrieval",
    kind: "Principles",
    slug: "retrieval-practice",
    title: "Active Recall: Learn by Retrieving, Then Checking",
    description:
      "Use closed-book questions, blank-page recall and answer checking to practise retrieving knowledge, with a science example and a manageable weekly routine.",
    intro:
      "A page can look familiar before you can explain it independently. Retrieval practice makes that difference visible: put the source aside, produce an answer, and then check what you actually knew. Start with material you have already been taught.",
    evidence:
      "A classroom research review found benefits across many retrieval-practice settings. Tasks and outcomes varied; this does not mean every quiz format works equally well.",
    sourceIds: ["retrieval"],
    methods: [
      {
        title: "Closed-book questions",
        text:
          "Write a question with a checkable answer. Say or write your response before opening the notes. Mark missing ideas, not just whether the answer sounds familiar.",
      },
      {
        title: "Blank-page recall",
        text:
          "Choose a narrow topic and reconstruct its key ideas on an empty page. Compare with your lesson material in a second colour. Turn gaps into questions for your next attempt.",
      },
      {
        title: "Application questions",
        text:
          "Include a question that asks you to use the idea in a new situation. Remembering a definition and explaining a case are different tasks; practise both when the course requires both.",
      },
    ],
    pattern: [
      "Choose three questions about a taught topic.",
      "Answer without the source, allowing an honest attempt.",
      "Check against the lesson or answer key and correct the gaps.",
      "Return to the questions in a later session without the corrections visible.",
    ],
    example: {
      title: "Example: retrieve the water cycle",
      paragraphs: [
        "Close your science notes. Answer: What is evaporation? What is condensation? How can water in a puddle become part of a cloud?",
        "A first answer might say, ‘The sun sucks the water up.’ Check it: liquid water changes into water vapour; when water vapour cools, it can condense into tiny droplets. Replace the misleading phrase with the process names.",
        "Now explain the puddle-to-cloud route in two sentences. Tomorrow, answer the same question without looking at today’s correction. If you can only repeat the wording, add a new case: why do droplets appear on a cold glass?",
      ],
    },
    transfer:
      "In history, retrieve two causes of an event and the evidence supporting each. A list of dates alone will not practise the explanation required by an essay.",
    pitfalls: [
      "Do not repeatedly rehearse an unchecked wrong answer. Ask a teacher when the key is unclear.",
      "For a younger learner, use two oral questions or a drawing and discuss the answer together. Increase the challenge gradually.",
    ],
    week: [
      {
        day: "Monday",
        task: "Water-cycle recall and corrections",
        minutes: 15,
      },
      {
        day: "Wednesday",
        task: "Water-cycle questions without notes",
        minutes: 10,
      },
      { day: "Friday", task: "Explain a new water-cycle example", minutes: 15 },
    ],
    subject: "Science",
    principleIds: [],
  },
  {
    id: "spacing",
    kind: "Principles",
    slug: "spaced-practice",
    title: "Spaced Practice: Build a Review Schedule That Lasts",
    description:
      "Spread review across several days, adjust it using recall results and build a realistic spaced study schedule instead of relying on one long revision session.",
    intro:
      "A review schedule makes room to return to a topic after some time away. Instead of finishing a unit and leaving it until the exam, give it a small place in later study sessions. Use the return visit to attempt something, not only reread it.",
    evidence:
      "Distributed practice received a high-utility rating in a broad review. Useful spacing depends on the material and how long it must be retained; there is no universal calendar interval.",
    sourceIds: ["review"],
    methods: [
      {
        title: "Delayed retrieval",
        text:
          "After learning a small topic, book a later session to answer its questions. Keep the first attempt closed-book, then correct it.",
      },
      {
        title: "Adjustable review",
        text:
          "When a question remains difficult, revisit it sooner with instruction or a simpler example. When it is reliably correct, leave a longer gap and check it again later.",
      },
      {
        title: "Cumulative review",
        text:
          "Reserve a few questions for earlier units alongside the current topic. Keep the workload small enough that older topics are actually revisited.",
      },
    ],
    pattern: [
      "Learn a manageable topic and prepare questions.",
      "Schedule two later review opportunities.",
      "Attempt the questions and record which need more help.",
      "Change the next review using the result and upcoming deadlines.",
    ],
    example: {
      title: "Example: revisiting geography vocabulary",
      paragraphs: [
        "On Monday, learn weathering and erosion using your class examples. Weathering breaks material down in place; erosion removes and transports it. Write one definition question and one comparison question.",
        "On Thursday, classify two cases: rock breaking apart in a crack, and a river carrying sediment downstream. The first illustrates weathering and the second erosion. Check the explanations, not just the labels.",
        "The following Monday, explain how both processes could affect the same hillside. If the distinction is still unclear, revisit the original diagrams with your teacher. This Monday–Thursday–Monday sequence is a planning example, not a prescribed memory formula.",
      ],
    },
    transfer:
      "For language vocabulary, alternate producing the word from its meaning with using it in a sentence. Spread both tasks across the week.",
    pitfalls: [
      "Spacing does not mean waiting until everything is forgotten. Use hints or reteaching when retrieval becomes unproductive.",
      "Do not create more review appointments than you can maintain. Reduce new material before letting a backlog grow indefinitely.",
    ],
    week: [
      {
        day: "Monday",
        task: "Learn geography terms and write questions",
        minutes: 20,
      },
      {
        day: "Thursday",
        task: "Recall and classify geography examples",
        minutes: 10,
      },
      {
        day: "Sunday",
        task: "Review results and book next week’s revisit",
        minutes: 10,
      },
    ],
    subject: "Geography",
    principleIds: [],
  },
  {
    id: "explanation",
    kind: "Principles",
    slug: "elaboration-self-explanation",
    title: "Self-Explanation: Connect Ideas and Explain Why",
    description:
      "Use why questions, worked-step explanations and concrete examples to check understanding, with an algebra demonstration and a short study pattern.",
    intro:
      "An explanation connects an answer to a reason. Ask what makes a step valid, which earlier idea it depends on, and what would change in a different example. Speaking simply can reveal missing reasoning, but simplicity is useful only when the explanation stays accurate.",
    evidence:
      "Research reviews identify self-explanation and elaborative questioning as promising, with benefits that depend on the task, guidance and prior knowledge.",
    sourceIds: ["review"],
    methods: [
      {
        title: "Explain each step",
        text:
          "Pause after a worked step and name the rule being used. Replace ‘because that is what comes next’ with a reason you could apply to another problem.",
      },
      {
        title: "Ask why and how",
        text:
          "Write a question about a relationship, rather than a label. Check your explanation against course material before treating it as a study answer.",
      },
      {
        title: "Contrast examples",
        text:
          "Choose a case that fits an idea and one that does not. Explain the difference. This makes a definition more precise than memorising its wording alone.",
      },
    ],
    pattern: [
      "Choose one idea or solution step.",
      "Explain the reason in your own words.",
      "Locate a gap or uncertain claim and check it.",
      "Revise the explanation and apply it to another example.",
    ],
    example: {
      title: "Example: why subtract from both sides?",
      paragraphs: [
        "Solve 3x + 6 = 18. Subtracting 6 from both sides gives 3x = 12, then dividing both sides by 3 gives x = 4. The important explanation is that doing the same operation to both sides preserves equality.",
        "‘Move the 6 and change the sign’ describes a shortcut but hides the reason. Write the full subtraction once: 3x + 6 − 6 = 18 − 6. Then explain why the sixes cancel on the left.",
        "Check by substitution: 3 × 4 + 6 = 18. Apply the explanation to 2x + 5 = 13, where x = 4 again. Equal answers do not make the two procedures identical; identify which numbers you subtract and divide by.",
      ],
    },
    transfer:
      "In English, explain how a particular word supports your interpretation of a character. Connect the quotation to the claim instead of simply placing them side by side.",
    pitfalls: [
      "Fluent explanations can contain mistakes. Verify the central claims rather than rewarding confidence alone.",
      "If the topic is new, explain a teacher-provided example first. You do not need to invent a complete explanation from nothing.",
    ],
    week: [
      {
        day: "Tuesday",
        task: "Explain three algebra solution steps",
        minutes: 20,
      },
      {
        day: "Thursday",
        task: "Apply the explanations to new equations",
        minutes: 15,
      },
      {
        day: "Saturday",
        task: "Explain one English quotation and check it",
        minutes: 15,
      },
    ],
    subject: "Math",
    principleIds: [],
  },
  {
    id: "interleaving",
    kind: "Principles",
    slug: "interleaving",
    title: "Interleaving: Learn to Choose the Right Approach",
    description:
      "Mix related problem types after initial learning so you practise selecting a method, with a geometry example and guidance on when mixing is useful.",
    intro:
      "A worksheet headed ‘area’ tells you which kind of calculation to use. A mixed set asks you to decide. Interleaving creates opportunities to distinguish related tasks once you have enough knowledge to attempt them.",
    evidence:
      "The learning-techniques review describes promising but task-dependent evidence for interleaving. It should not be interpreted as a reason to switch constantly between unrelated activities.",
    sourceIds: ["review"],
    methods: [
      {
        title: "Mixed problem sets",
        text:
          "Combine two or three related question types without section labels revealing the solution method. State the required quantity before calculating.",
      },
      {
        title: "Compare near neighbours",
        text:
          "Place similar-looking questions together and explain what changes the choice of method. Focus on the relevant cue in the question.",
      },
      {
        title: "Revisit an earlier type",
        text:
          "Include a previously learned question among current ones. If you cannot begin it, return to a worked example rather than repeatedly guessing.",
      },
    ],
    pattern: [
      "Practise each unfamiliar type with support first.",
      "Mix a small number of related types.",
      "Name the method and justify your choice before solving.",
      "Review selection errors separately from arithmetic errors.",
    ],
    example: {
      title: "Example: area or perimeter?",
      paragraphs: [
        "A rectangle is 6 cm long and 4 cm wide. Question A asks for the ribbon needed around its edge: perimeter = 2 × (6 + 4) = 20 cm. Question B asks for the paper needed to cover it: area = 6 × 4 = 24 cm².",
        "Before calculating, underline ‘around’ or ‘cover’ and identify the quantity. Then try a 5 cm by 3 cm rectangle with the question order reversed: covering needs 15 cm²; edging needs 16 cm.",
        "Finally remove the cue words: a gardener wants a fence around a rectangular plot. Explain why this asks for perimeter. Record a wrong formula choice separately from a multiplication slip.",
      ],
    },
    transfer:
      "In a language lesson, mix sentences requiring two verb forms and explain the context that selects each form. Learn the forms first, then practise choosing between them.",
    pitfalls: [
      "Mixing everything at once can obscure the skill being learned. Start with a small family of related tasks.",
      "Keep the session focused. Interleaving problems does not require checking messages or changing apps between questions.",
    ],
    week: [
      {
        day: "Monday",
        task: "Learn area and perimeter with examples",
        minutes: 20,
      },
      {
        day: "Wednesday",
        task: "Solve a mixed area/perimeter set",
        minutes: 15,
      },
      {
        day: "Friday",
        task: "Retry method-choice errors with new shapes",
        minutes: 15,
      },
    ],
    subject: "Math",
    principleIds: [],
  },
  {
    id: "examples",
    kind: "Principles",
    slug: "worked-examples",
    title: "Worked Examples: Move from Guidance to Independent Practice",
    description:
      "Learn a procedure through worked solutions, missing-step problems and independent attempts, with a maths example and a plan for reducing support.",
    intro:
      "When a procedure is unfamiliar, a complete solution gives you something concrete to inspect. Your job is to understand the decisions, then gradually take over those decisions yourself. Reading and independent solving play different roles in the session.",
    evidence:
      "The IES practice guide recommends alternating worked solutions with problem-solving exercises. The amount of support should fit the learner’s current knowledge.",
    sourceIds: ["ies"],
    methods: [
      {
        title: "Annotate a model",
        text:
          "Write a short reason beside each important step of a solved question. Check symbols, units and the final answer.",
      },
      {
        title: "Complete missing steps",
        text:
          "Use a partially solved question. Supply a missing operation or calculation, then compare it with a full solution.",
      },
      {
        title: "Fade the support",
        text:
          "Move from a complete model to a partial one and then an independent problem. Return to support when a specific gap appears.",
      },
    ],
    pattern: [
      "Study one complete solution.",
      "Explain its key decisions.",
      "Finish a related incomplete solution.",
      "Solve another problem without the model, then check.",
    ],
    example: {
      title: "Example: calculate a unit price",
      paragraphs: [
        "A pack of 4 notebooks costs $12. The worked solution is $12 ÷ 4 = $3 per notebook. Division finds the cost of one item when the total covers equal-priced items.",
        "Completion problem: 5 notebooks cost $20, so the unit price is $20 ÷ __ = $__ per notebook. Fill the blanks with 5 and 4. Explain why multiplying the price by the count would answer the wrong question.",
        "Independent attempt: 3 notebooks cost $15. Hide the model and calculate $5 per notebook. Check by reversing the calculation: 3 × $5 = $15. Then decide which pack is cheaper per notebook, rather than which has the smaller total price.",
      ],
    },
    transfer:
      "In science, study a worked speed calculation with units, complete a missing time value, and then solve a new distance/time problem independently.",
    pitfalls: [
      "Do not copy the model and count that as an independent answer. Hide it for the final attempt.",
      "A student who already solves the task reliably may need varied problems rather than more complete models.",
    ],
    week: [
      {
        day: "Monday",
        task: "Annotate a unit-price worked example",
        minutes: 15,
      },
      {
        day: "Wednesday",
        task: "Complete missing steps and solve independently",
        minutes: 20,
      },
      {
        day: "Friday",
        task: "Compare three new shopping examples",
        minutes: 15,
      },
    ],
    subject: "Math",
    principleIds: [],
  },
  {
    id: "visuals",
    kind: "Principles",
    slug: "words-and-visuals",
    title: "Words and Visuals: Make Concepts Easier to Understand",
    description:
      "Connect diagrams, labels, examples and explanations to study concepts, with a water-cycle task and practical checks for useful rather than decorative visuals.",
    intro:
      "A diagram is useful when it shows a relationship you can also explain. Turn arrows, labels and positions into sentences, and turn the sentences back into a drawing. The aim is a clear model of the idea, not an attractive page of notes.",
    evidence:
      "IES recommends combining graphics with verbal descriptions and connecting concrete with abstract representations. These recommendations do not establish fixed visual or verbal learner types.",
    sourceIds: ["ies"],
    methods: [
      {
        title: "Label the relationship",
        text:
          "Give each arrow a meaning: causes, moves to, increases or comes before. Avoid lines whose purpose you cannot explain.",
      },
      {
        title: "Translate representations",
        text:
          "Describe a diagram in words, then redraw it from that description. Compare the two for missing steps or misleading arrangements.",
      },
      {
        title: "Reconstruct and check",
        text:
          "Hide the original diagram, draw its essential parts and label them. Correct the relationships before adding colour or decoration.",
      },
    ],
    pattern: [
      "Inspect the model and its explanation.",
      "Describe what each important mark means.",
      "Reconstruct the model without looking.",
      "Check and repair the relationships.",
    ],
    example: {
      title: "Example: draw a process, not a picture",
      paragraphs: [
        "Sketch a puddle, an arrow rising from it, and a cloud. Label the arrow ‘evaporation: liquid water becomes water vapour’. Add ‘condensation: water vapour becomes droplets’ at the cloud. The arrow does not mean the sun pulls up liquid water.",
        "Add an arrow from the cloud to the ground labelled ‘precipitation’. Write one sentence explaining each transition. Check whether the diagram implies that all water follows the same route; it is a simplified model, not a map of every pathway.",
        "Cover the sketch and rebuild it from the three process names. Compare labels and directions. An accurate rough sketch is more useful for this task than a beautiful picture with an incorrect arrow.",
      ],
    },
    transfer:
      "For history, use a timeline to show order, then a separate explanation for causation. Events placed next to one another are not automatically cause and effect.",
    pitfalls: [
      "Do not add unrelated images that compete with the concept. Keep labels close to what they describe.",
      "If drawing is difficult, use a supplied diagram and explain its parts orally or with labels. Artistic skill is not the target.",
    ],
    week: [
      {
        day: "Tuesday",
        task: "Label and explain a science process diagram",
        minutes: 20,
      },
      {
        day: "Thursday",
        task: "Reconstruct the diagram and check arrows",
        minutes: 15,
      },
      {
        day: "Saturday",
        task: "Build a history timeline with explanations",
        minutes: 15,
      },
    ],
    subject: "Science",
    principleIds: [],
  },
  {
    id: "feedback",
    kind: "Principles",
    slug: "feedback-and-correction",
    title: "Feedback and Error Correction: Turn Mistakes into Better Practice",
    description:
      "Use feedback to diagnose mistakes and choose a useful next attempt. Includes a fraction correction, error categories and a delayed retry routine.",
    intro:
      "A mark tells you whether an answer met a criterion. Useful follow-up asks what needs changing and gives you a chance to make that change. Keep the original attempt so you can compare the reasoning before and after the correction.",
    evidence:
      "A meta-analysis of educational feedback found substantial variation in effects. The information feedback provides matters; a mark, praise and an explanation are not interchangeable interventions.",
    sourceIds: ["feedback"],
    methods: [
      {
        title: "Diagnose the error",
        text:
          "Ask whether the problem involved missing knowledge, choosing a method, executing it, or interpreting the question. Describe the specific gap instead of writing ‘careless’.",
      },
      {
        title: "Write a usable correction",
        text:
          "Record the rule or reasoning you missed and one correct example. If you cannot explain the correction, ask for help before practising it again.",
      },
      {
        title: "Retry with a new item",
        text:
          "Solve a similar question without copying the corrected answer. Later, include the skill in a mixed set so you also practise recognising when to use it.",
      },
    ],
    pattern: [
      "Attempt the task and keep your reasoning.",
      "Compare it with criteria or reliable feedback.",
      "Explain the correction and practise a similar task.",
      "Schedule a delayed retry and check whether the error recurs.",
    ],
    example: {
      title: "Example: repair a fraction misconception",
      paragraphs: [
        "A student writes 1/2 + 1/3 = 2/5. This is not simply a calculation slip: the denominators describe different-sized parts, so adding them directly does not preserve the quantities.",
        "Use sixths: 1/2 = 3/6 and 1/3 = 2/6. Therefore the sum is 5/6. Draw a bar split into six equal parts if the equivalence is unclear. Record the correction: express both fractions in a common unit before adding their numerators.",
        "Retry with 1/4 + 1/2. The correct answer is 3/4 because 1/2 = 2/4. Two days later, try 1/3 + 1/6 = 1/2 without viewing the correction. The delayed attempt tells you more than copying 5/6 immediately.",
      ],
    },
    transfer:
      "In English, turn ‘explain your evidence’ into a concrete revision: add a sentence connecting the quotation to the paragraph’s claim, then check it against the teacher’s criteria.",
    pitfalls: [
      "Do not fill an error log with labels and never return to it. Every entry needs a next action.",
      "When feedback conflicts or seems incomplete, resolve it with a teacher rather than guessing which answer to memorise.",
    ],
    week: [
      {
        day: "Monday",
        task: "Diagnose fraction errors and write corrections",
        minutes: 20,
      },
      {
        day: "Wednesday",
        task: "Retry fraction questions without the log",
        minutes: 15,
      },
      {
        day: "Friday",
        task: "Check a mixed set and update the error log",
        minutes: 15,
      },
    ],
    subject: "Math",
    principleIds: [],
  },
  {
    id: "regulation",
    kind: "Principles",
    slug: "plan-monitor-evaluate",
    title: "Plan, Monitor, Evaluate: Manage Your Own Learning",
    description:
      "Set concrete study goals, use performance checks and adjust the next session. Build a weekly review that measures understanding rather than time spent.",
    intro:
      "A study plan is a starting decision, not a promise to keep doing the same thing regardless of results. Decide what you want to do independently, inspect your progress, and use the evidence to change the next task.",
    evidence:
      "EEF guidance supports explicitly teaching planning, monitoring and evaluation within learning tasks. Learners often need modelling and support before using these strategies independently.",
    sourceIds: ["regulation"],
    methods: [
      {
        title: "Set a performance goal",
        text:
          "Replace ‘study science’ with a checkable outcome such as ‘explain two changes of state without notes’. Choose a task that will show whether you can do it.",
      },
      {
        title: "Monitor with a small check",
        text:
          "Pause for a question, a problem or a short explanation. Use the answer to decide whether to continue, simplify the task or seek help.",
      },
      {
        title: "Evaluate the next action",
        text:
          "At the end, record what worked, what remains uncertain and the next task. Keep the reflection brief enough to use regularly.",
      },
    ],
    pattern: [
      "Choose a specific outcome and a suitable method.",
      "Attempt a small performance check.",
      "Compare the result with the goal.",
      "Book a next action that addresses the result.",
    ],
    example: {
      title: "Example: plan an evidence-based paragraph",
      paragraphs: [
        "Goal: write one history paragraph that makes a claim, supports it with evidence from the assigned sources, and explains the connection. Before writing, identify the marking criteria and choose the relevant source.",
        "After the first draft, label the claim C, evidence E and explanation X. Suppose the paragraph has C and E but no X. The next action is to explain how the evidence supports the claim, not to spend another twenty minutes rereading the chapter.",
        "Revise and ask a teacher or peer to check the explanation against the criteria. At the weekly review, try another paragraph on a different question. Record whether you can supply the connection independently.",
      ],
    },
    transfer:
      "For maths, count which problems you can solve and explain without a model. If method selection is the gap, plan a small mixed set rather than more copies of one type.",
    pitfalls: [
      "Do not use a completed timer or a full notebook as the only success measure. Choose a check related to the learning goal.",
      "For younger learners, offer two task choices and model the reflection aloud. Independence can develop gradually.",
    ],
    week: [
      {
        day: "Monday",
        task: "Set a paragraph goal and draft one answer",
        minutes: 25,
      },
      {
        day: "Wednesday",
        task: "Check criteria and revise the explanation",
        minutes: 20,
      },
      {
        day: "Sunday",
        task: "Review results and choose next week’s tasks",
        minutes: 15,
      },
    ],
    subject: "History",
    principleIds: [],
  },
]

export const systems: StudySkill[] = [
  {
    id: "leitner",
    kind: "Systems",
    slug: "leitner-flashcards",
    title: "The Leitner System: Organise Flashcards for Spaced Recall",
    description:
      "Set up a small Leitner flashcard routine with honest recall, answer checks and adjustable review intervals, including an example card and weekly schedule.",
    intro:
      "Use a Leitner routine when you need to revisit short, checkable answers without giving every card equal time. It is a way to organise flashcard practice; it does not replace explanations, reading or longer problem solving.",
    evidence:
      "Kansas State describes a three-stack routine. Its instructions are a procedural source; research support for retrieval and spacing does not prove that one particular box schedule is optimal.",
    sourceIds: ["leitner", "retrieval", "review"],
    principleIds: ["retrieval", "spacing", "feedback"],
    subject: "Science",
    effort: "A small card set and three labelled stacks",
    setup:
      "Gather index cards, reliable lesson answers and three envelopes or dividers. Start with six to ten cards on one topic. The following is a simple adaptation: missed cards return to the first stack; successful cards move towards less frequent review.",
    methods: [
      {
        title: "Write one question per card",
        text:
          "Make the front specific enough to answer without guessing what is wanted. Put the answer and a short explanation on the back. Avoid copying a paragraph that you will merely recognise.",
      },
      {
        title: "Attempt before turning",
        text:
          "Say or write the answer before revealing it. Compare meaning, not exact wording, unless the course requires precise terminology.",
      },
      {
        title: "Move and schedule",
        text:
          "Move a correct card forward one stack; return a missed card to the first. Review the first stack most often and the others less often. Choose manageable dates and revise them when recall is too easy or too difficult.",
      },
    ],
    pattern: [
      "Collect the stacks due today.",
      "Answer each card before checking the back.",
      "Correct mistakes and move the cards according to the chosen rule.",
      "Record the next review dates and stop before creating an unmanageable backlog.",
    ],
    example: {
      title: "Example: a card that tests a distinction",
      paragraphs: [
        "Front: ‘How is evaporation different from condensation?’ Back: ‘Evaporation changes liquid water to water vapour; condensation changes water vapour to liquid droplets.’ Add an example of each from the lesson.",
        "On Monday, you reverse the terms. Check the card, explain the correction and keep it in stack 1. On Tuesday, you answer correctly before turning it over, so move it to stack 2 and book another attempt for Thursday.",
        "If Thursday’s answer is correct, move it to stack 3 for a later check. If it is wrong, return it to stack 1. Add a separate application question about droplets on a cold glass; knowing the terms alone does not demonstrate that application.",
      ],
    },
    transfer:
      "For English vocabulary, put a word’s meaning on the front and ask for the word plus an original sentence. Check the sentence as well as the label.",
    pitfalls: [
      "A familiar-looking answer is not a successful recall attempt. Make the decision before turning the card.",
      "The three-stack schedule is adjustable. For complex maths or essay skills, add full practice questions rather than expanding every answer into a crowded card.",
    ],
    week: [
      {
        day: "Monday",
        task: "Make and practise six science flashcards",
        minutes: 15,
      },
      { day: "Tuesday", task: "Review difficult science cards", minutes: 10 },
      {
        day: "Thursday",
        task: "Review due cards and one application question",
        minutes: 15,
      },
    ],
  },
  {
    id: "cornell",
    kind: "Systems",
    slug: "cornell-notes",
    title: "Cornell Notes: Turn Lesson Notes into Questions and Review",
    description:
      "Use Cornell notes to turn a lesson into cue questions, covered-note recall and a later review, with a complete science example and practical adaptations.",
    intro:
      "Cornell notes give a lesson record a second job: helping you test what you can explain. The page layout is a starting structure. The questions and later attempts make it part of a study routine.",
    evidence:
      "Cornell’s Learning Strategies Center provides the system instructions. Treat the layout as an organiser; evidence for recall does not establish that a particular page format is universally superior.",
    sourceIds: ["cornell", "retrieval"],
    principleIds: ["retrieval", "spacing", "explanation"],
    optionalPrincipleIds: ["visuals"],
    subject: "Science",
    effort: "One divided notebook page",
    setup:
      "Leave a wide area for notes, a narrower cue column and a summary space below. A digital document with two columns also works. Keep the layout legible rather than spending the study session decorating it.",
    methods: [
      {
        title: "Record then question",
        text:
          "Capture the lesson’s main ideas. Afterwards, turn them into questions in the cue column; make them answerable without reading the notes.",
      },
      {
        title: "Cover and respond",
        text:
          "Cover the notes, look at the cues and explain the answers. Uncover to check, then mark the ideas that need attention.",
      },
      {
        title: "Summarise and revisit",
        text:
          "Write a short summary connecting the ideas. Return to the cues on another day. A labelled diagram is an optional addition when it helps explain a relationship.",
      },
    ],
    pattern: [
      "Record the lesson’s main ideas.",
      "Create cue questions and a short summary.",
      "Cover the notes, answer the cues and check.",
      "Use the same cues for a later closed-note review.",
    ],
    example: {
      title: "Example: a page about changes of state",
      paragraphs: [
        "Notes area: ‘Melting: solid to liquid. Freezing: liquid to solid. Evaporation: liquid to gas. Condensation: gas to liquid.’ Add the teacher’s examples beside each transition.",
        "Cue column: ‘What changes when ice melts?’ ‘What is the reverse transition?’ ‘Why can water droplets form outside a cold glass?’ Cover the notes. A suitable answer to the last cue is that water vapour in surrounding air cools and condenses on the cold surface; it is not water leaking through the glass.",
        "Summary: ‘Water can change state; the names describe the direction of change.’ On Thursday, answer the cues again and add a fresh example. If you confuse evaporation and condensation, use a simple labelled arrow diagram before another attempt.",
      ],
    },
    transfer:
      "For an English text, use cues about a character’s choices and evidence. Answer with a quotation and an explanation rather than a plot summary alone.",
    pitfalls: [
      "Do not make the cue column a duplicate of the notes. It should prompt an answer.",
      "A complete page is not the end of the process. If recall fails, revise the explanation or ask for help before adding more notes.",
    ],
    week: [
      {
        day: "Monday",
        task: "Create science Cornell notes and cues",
        minutes: 20,
      },
      {
        day: "Wednesday",
        task: "Answer Cornell cues with notes covered",
        minutes: 15,
      },
      {
        day: "Friday",
        task: "Review difficult cues and add one new example",
        minutes: 15,
      },
    ],
  },
  {
    id: "sq3r",
    kind: "Systems",
    slug: "sq3r-reading",
    title: "SQ3R: A Practical System for Studying a Textbook Chapter",
    description:
      "Try Survey, Question, Read, Recite and Review with a worked reading example. Turn a textbook section into questions and checkable explanations.",
    intro:
      "Choose SQ3R when a textbook chapter feels like a long stream of facts. Its sequence gives you a purpose before reading and a way to check what you understood afterwards. Work on a short section before applying it to an entire chapter.",
    evidence:
      "Cornell describes SQ3R as a textbook-reading system. The procedure combines several learning activities; following its acronym alone is not evidence that a learner has understood the text.",
    sourceIds: ["reading"],
    principleIds: ["retrieval", "explanation", "regulation"],
    optionalPrincipleIds: ["spacing"],
    subject: "History",
    effort: "A textbook section and a question sheet",
    setup:
      "Use the assigned chapter, paper for questions and the lesson’s learning goals. For younger readers, have an adult help select a short section and turn a heading into a question.",
    methods: [
      {
        title: "Survey and question",
        text:
          "Preview the headings, diagrams and summary. Turn a heading into a question that the section might answer.",
      },
      {
        title: "Read and recite",
        text:
          "Read to answer your question. Close the text and explain the answer, then check that the text supports it.",
      },
      {
        title: "Review the whole",
        text:
          "Connect the section answers to the chapter’s main argument. Delayed review is an optional scheduling extension: book another recall attempt later in the week.",
      },
    ],
    pattern: [
      "Survey the structure before reading closely.",
      "Question what each selected section will explain.",
      "Read one section with those questions in mind.",
      "Recite the answer without the text, then check it.",
      "Review the main connections and unresolved questions.",
    ],
    example: {
      title: "Example: read for causes and consequences",
      paragraphs: [
        "Practice passage: ‘A town grew beside a river crossing. Travellers stopped there to trade. Later, a bridge made crossing easier, and more shops opened nearby.’ This invented passage lets you practise the routine before using an assigned history text.",
        "Survey the topic, then ask: ‘Why did trade develop here?’ Read and recite: ‘The crossing brought travellers together, creating customers for trade.’ Check that the passage supports the claim. A second answer is that the bridge made crossing easier and was followed by more shops.",
        "During review, distinguish the passage’s evidence from an inference: it does not tell you that every resident became wealthy. For a real chapter, attach page references to your answers and note where a causal claim requires more evidence.",
      ],
    },
    transfer:
      "For science reading, turn ‘Insulators and conductors’ into ‘How do these materials differ, and what example demonstrates the difference?’ Check the explanation, not just the vocabulary.",
    pitfalls: [
      "Do not write a question for every sentence. Choose questions that match the assignment’s purpose.",
      "Reciting a sentence without understanding it can hide a gap. Explain an example or contrast to check the meaning.",
    ],
    week: [
      {
        day: "Tuesday",
        task: "Survey and question one history section",
        minutes: 15,
      },
      {
        day: "Wednesday",
        task: "Read, recite and check the section answers",
        minutes: 25,
      },
      {
        day: "Saturday",
        task: "Review chapter connections without the text",
        minutes: 15,
      },
    ],
  },
  {
    id: "feynman",
    kind: "Systems",
    slug: "feynman-technique",
    title: "The Feynman Technique: Explain an Idea and Find the Gaps",
    description:
      "Use a Feynman-style explanation workflow to uncover gaps, verify claims and refine your understanding, with a simple circuit example and study schedule.",
    intro:
      "The popular Feynman-style workflow asks you to explain an idea plainly, locate gaps, check them and try again. Use it when you can repeat terminology but struggle to explain what it means in a concrete situation.",
    evidence:
      "This guide uses a popular workflow label, not a verified historical checklist or an independently validated package. Its learning rationale comes from self-explanation and retrieval research.",
    sourceIds: ["review", "retrieval"],
    principleIds: ["explanation", "retrieval", "feedback"],
    optionalPrincipleIds: ["visuals"],
    subject: "Science",
    effort: "One concept, blank paper and a reliable reference",
    setup:
      "Choose one narrow question and a reader who has less background than you. Keep a textbook or teacher explanation ready for checking. You do not need a real audience to write the first explanation.",
    methods: [
      {
        title: "Explain from memory",
        text:
          "Write a short explanation using ordinary language. Define necessary technical words instead of removing them at the cost of accuracy.",
      },
      {
        title: "Find and verify gaps",
        text:
          "Underline any step you cannot justify. Check a reliable source, then correct the explanation rather than hiding the gap with an analogy.",
      },
      {
        title: "Refine and apply",
        text:
          "Explain again and test a new case. A drawing is an optional aid if its labels and relationships agree with the words.",
      },
    ],
    pattern: [
      "Pick a precise question.",
      "Explain it without the source.",
      "Check uncertain claims and repair the reasoning.",
      "Explain again and use the idea in another case.",
    ],
    example: {
      title: "Example: why does opening a switch turn off a lamp?",
      paragraphs: [
        "A first explanation says, ‘The battery sends electricity to the lamp, and the switch stops it.’ That leaves a gap: why must the rest of the circuit also be connected?",
        "After checking the class model, revise: ‘In this simple battery circuit, current needs a complete conducting path through the lamp and back to the battery. Opening the switch breaks that path, so current does not flow through the lamp.’ Use a circuit drawing to show the path if helpful.",
        "Now ask what happens if a wire elsewhere in the same simple series circuit is disconnected. The path is again broken. Avoid saying that the lamp uses up the current: the circuit transfers energy, and that distinction matters when you explain it further.",
      ],
    },
    transfer:
      "In geography, explain the difference between weathering and erosion to someone who knows neither term. Add a case and identify which process it demonstrates.",
    pitfalls: [
      "Simple wording is not a licence to make a false analogy. State where a comparison stops working.",
      "Do not treat teaching aloud as sufficient checking. Verify the content before rehearsing it again.",
    ],
    week: [
      {
        day: "Monday",
        task: "Explain a simple circuit from memory",
        minutes: 15,
      },
      {
        day: "Wednesday",
        task: "Check circuit gaps and redraw the model",
        minutes: 20,
      },
      {
        day: "Friday",
        task: "Explain a new circuit case without notes",
        minutes: 15,
      },
    ],
  },
  {
    id: "pomodoro",
    kind: "Systems",
    slug: "pomodoro-study",
    title: "Pomodoro for Studying: Plan Focused Work and Useful Breaks",
    description:
      "Organise study blocks with the Pomodoro approach while choosing effective work inside the timer. Includes a realistic session, adaptations and evidence limits.",
    intro:
      "A timer can make a study appointment easier to start and stop. It cannot decide whether the work is useful. Choose the learning task first, then use work and break periods to organise your effort.",
    evidence:
      "The creator’s site describes the technique. A 2025 study of university students did not find productivity or task-completion differences between the compared break strategies; fixed timing is not a universal optimum.",
    sourceIds: ["pomodoro", "breaks"],
    principleIds: ["regulation"],
    optionalPrincipleIds: ["retrieval", "examples"],
    subject: "English",
    effort: "A timer and one clearly defined task",
    setup:
      "Prepare the materials and put unrelated notifications aside. The familiar starting pattern is 25 minutes of work and a 5-minute break, with a longer break after several cycles. A shorter block for a younger learner is an adaptation, not a rule everyone must follow.",
    methods: [
      {
        title: "Choose an outcome",
        text:
          "Write one task you can inspect: answer five questions, explain two examples or revise one paragraph. ‘Be productive’ does not tell you where to begin.",
      },
      {
        title: "Protect the block",
        text:
          "Work on the chosen task. Note interruptions to handle later. If you get stuck, identify a useful help-seeking action rather than waiting out the timer.",
      },
      {
        title: "Break and evaluate",
        text:
          "Take the planned break, then check what the work produced. Change the next block if the task, support or duration was unsuitable.",
      },
    ],
    pattern: [
      "Select a specific task and prepare materials.",
      "Work for one planned block.",
      "Take a short break away from the task.",
      "Check the result and choose whether another block is useful.",
    ],
    example: {
      title: "Example: a 55-minute English session",
      paragraphs: [
        "Block 1, 25 minutes: answer three questions about an assigned passage without your notes, then check the text. Suppose one answer gives a quotation but no explanation. Mark that as the next task rather than rushing to a new passage.",
        "Break, 5 minutes: stand up, get water or rest your eyes. Block 2, 25 minutes: rewrite the incomplete answer and explain how the quotation supports the claim. Finish by noting what remains unclear.",
        "The completed timer records time, not comprehension. Compare the revised response with the question and teacher’s criteria. If the session is too long after school, choose a shorter adapted block and keep the same concrete task.",
      ],
    },
    transfer:
      "For maths, put worked-example practice inside a block. The timer organises the session; the examples, independent attempt and checking provide the learning activity.",
    pitfalls: [
      "Do not cut off a useful explanation just to satisfy an arbitrary count. Adapt the schedule to the task and learner.",
      "Breaks that become long scrolling sessions can prevent restarting. Decide what the break will involve before beginning.",
    ],
    week: [
      {
        day: "Tuesday",
        task: "English: two 25-minute blocks with a 5-minute break",
        minutes: 55,
      },
      {
        day: "Thursday",
        task: "One focused paragraph revision block",
        minutes: 25,
      },
      {
        day: "Sunday",
        task: "Review whether the block lengths helped",
        minutes: 10,
      },
    ],
  },
  {
    id: "example-cycle",
    kind: "Systems",
    slug: "worked-example-practice",
    title: "A Worked-Example Practice Cycle for Maths and Science",
    description:
      "Follow a complete algebra practice cycle from a solved example to missing steps, independent work and later mixed questions, with answer checks included.",
    intro:
      "Use this workflow for a procedure you are beginning to learn. It gives you a route from understanding a model to choosing and carrying out the steps yourself. The goal is to need less support as your understanding grows.",
    evidence:
      "This is a School Calendar example workflow, not a separately tested programme. It combines the worked-example recommendation with explanation, checking and later variation.",
    sourceIds: ["ies", "review", "feedback"],
    principleIds: ["examples", "explanation", "feedback"],
    optionalPrincipleIds: ["interleaving", "visuals"],
    subject: "Math",
    editorial: true,
    effort: "A solved example and three related questions",
    setup:
      "Use a teacher-approved model with every step visible. Prepare a completion question and an independent question at a similar level. Save a slightly different question for a later session.",
    methods: [
      {
        title: "Inspect the model",
        text:
          "Identify the starting information, target and operation at each step. Explain why the operation is valid.",
      },
      {
        title: "Complete then solve",
        text:
          "Fill in a missing step before attempting a fully independent question. Use feedback to locate the earliest step that became uncertain.",
      },
      {
        title: "Add selection practice later",
        text:
          "Once the procedure is familiar, add another related type. This optional mixed stage practises choosing an approach as well as executing it.",
      },
    ],
    pattern: [
      "Read and explain a complete example.",
      "Complete a partial solution.",
      "Solve a related problem with the model covered.",
      "Check the answer and correct the earliest error.",
      "Return later to a mixed set when ready.",
    ],
    example: {
      title: "Example: from 2x + 3 = 11 to independent solving",
      paragraphs: [
        "Model: 2x + 3 = 11. Subtract 3 from both sides: 2x = 8. Divide both sides by 2: x = 4. Check: 2 × 4 + 3 = 11. Explain that each operation preserves equality.",
        "Completion: 3x + 2 = 14 → 3x = __ → x = __. The blanks are 12 and 4. If you wrote 16, revisit why 2 is subtracted from both sides.",
        "Independent: 4x + 1 = 21. With the model hidden, subtract 1 and divide by 4 to get x = 5. Check in the original equation. A later mixed question, x/2 + 1 = 6, gives x = 10; here the final operation is multiplication, so copying the earlier pattern without thinking would fail.",
      ],
    },
    transfer:
      "For science speed calculations, follow a model using distance ÷ time, complete a missing substitution, then solve with new values and check the units.",
    pitfalls: [
      "Do not introduce a harder independent question before the learner understands the model. Change one demand at a time.",
      "If independent work is already reliable, move to varied applications rather than repeating the same fully worked solution.",
    ],
    week: [
      {
        day: "Monday",
        task: "Explain one equation model and complete two solutions",
        minutes: 20,
      },
      {
        day: "Wednesday",
        task: "Solve and check three equations independently",
        minutes: 20,
      },
      {
        day: "Friday",
        task: "Choose methods in a mixed equation set",
        minutes: 20,
      },
    ],
  },
  {
    id: "error-log",
    kind: "Systems",
    slug: "error-log-review",
    title: "An Error-Log Review System: Correct, Retry, Revisit",
    description:
      "Build an error log that leads to action: diagnose mistakes, explain corrections, retry new questions and revisit them later using a worked maths example.",
    intro:
      "An error log should be a small queue of useful next actions. Keep only enough detail to understand what happened and practise the correction. The point is to change the next attempt, not collect a permanent catalogue of mistakes.",
    evidence:
      "This School Calendar workflow applies feedback and self-regulation principles. Research on those components does not validate this exact log format or timetable.",
    sourceIds: ["feedback", "regulation"],
    principleIds: ["feedback", "regulation", "retrieval", "spacing"],
    optionalPrincipleIds: ["interleaving"],
    subject: "Math",
    editorial: true,
    effort: "A marked task and a short review log",
    setup:
      "Use five columns: task, what went wrong, corrected reasoning, next attempt, revisit date. Choose two or three errors that matter for the next learning goal. Keep a reliable answer or teacher comment beside the original work.",
    methods: [
      {
        title: "Classify precisely",
        text:
          "Distinguish a misconception, a method-choice error and an execution slip. Name the actual step that needs work.",
      },
      {
        title: "Connect correction to action",
        text:
          "For each entry, write a short explanation and choose a fresh problem. A copied answer without another attempt leaves the loop unfinished.",
      },
      {
        title: "Retest and retire",
        text:
          "Try the fresh problem without viewing the log. Return later, optionally in a mixed set. Archive an entry once performance is reliable, and bring it back if needed.",
      },
    ],
    pattern: [
      "Select a small number of important errors.",
      "Explain the correction using a reliable reference.",
      "Solve a similar new task without the correction visible.",
      "Book a delayed check and update the log from the result.",
    ],
    example: {
      title: "Example: diagnose before practising",
      paragraphs: [
        "Original task: solve 3x + 4 = 19. Response: 3x = 23. Log entry: ‘I added 4 instead of undoing the addition. Subtract 4 from both sides, giving 3x = 15 and x = 5.’ Verify: 3 × 5 + 4 = 19.",
        "Next attempt: 2x + 7 = 17. Solve independently to get x = 5. If the first step is still wrong, inspect a worked example with a teacher instead of merely trying more questions.",
        "A different entry might be 6 × 7 = 48: the equation method may be sound but the multiplication needs checking. A third might use area when a fence length was requested: that needs method-selection practice. Do not give all three errors the same repair task.",
      ],
    },
    transfer:
      "For an essay, log a missing link between evidence and claim. The next attempt should be a new explanation sentence or paragraph checked against the rubric.",
    pitfalls: [
      "Do not label every error ‘careless’. That does not identify an action the learner can take.",
      "An overflowing log is difficult to use. Prioritise recurring or foundational gaps and ask for help with persistent misconceptions.",
    ],
    week: [
      {
        day: "Monday",
        task: "Choose three quiz errors and write repair tasks",
        minutes: 20,
      },
      {
        day: "Wednesday",
        task: "Attempt fresh questions without the error log",
        minutes: 20,
      },
      {
        day: "Saturday",
        task: "Delayed mixed check and archive resolved errors",
        minutes: 15,
      },
    ],
  },
  {
    id: "weekly-review",
    kind: "Systems",
    slug: "weekly-spaced-review",
    title: "Build a Weekly Study System with Recall, Spacing and Review",
    description:
      "Combine recall, feedback and spaced sessions across three subjects. Use an adaptable weekly study calendar and a practical end-of-week review.",
    intro:
      "A weekly system helps you choose what to revisit as well as when to study. Start with a few specific tasks, leave room for homework and rest, and use the results to change next week’s plan.",
    evidence:
      "This is an example schedule built from retrieval, spacing and self-regulation principles. Its days and durations are editorial choices, not a tested prescription for every student.",
    sourceIds: ["retrieval", "review", "regulation"],
    principleIds: ["spacing", "retrieval", "regulation", "feedback"],
    optionalPrincipleIds: ["interleaving"],
    subject: "Geography",
    editorial: true,
    effort: "Three subject goals and a weekly calendar",
    setup:
      "List deadlines and existing commitments first. Pick one goal each for science, maths and English. Prepare questions and answer criteria before booking the sessions so that every event has a clear task.",
    methods: [
      {
        title: "Choose measurable goals",
        text:
          "Write outcomes such as explain two changes of state, solve three equations, or justify an interpretation with evidence. Keep them small enough to inspect.",
      },
      {
        title: "Place returns in the week",
        text:
          "Schedule a first attempt and a later revisit. Give persistent gaps a supported repair session. Mixed questions can be added once individual procedures are familiar.",
      },
      {
        title: "Review and adjust",
        text:
          "At the end of the week, examine the work rather than only the calendar’s completed slots. Keep, shorten or replace tasks using what you can now do independently.",
      },
    ],
    pattern: [
      "Choose a few goals and prepare checks.",
      "Schedule focused attempts around existing commitments.",
      "Return to earlier material after a gap.",
      "Review results and choose next week’s priorities.",
    ],
    example: {
      title: "Example: one week, three subjects",
      paragraphs: [
        "Monday: answer five science questions about changes of state, check them and flag two uncertain answers. Tuesday: solve three equations and compare each step with the key. These are the first checks, not an assumption that both topics need equal time.",
        "Thursday: return to the two science gaps and attempt a new example. Friday: write an English paragraph using a quotation and explain how it supports the claim. Check against the teacher’s criteria. If maths method selection was weak, add a small mixed set next week.",
        "Sunday: record one thing you can now do, one remaining gap and one next action per subject. Move unfinished work deliberately rather than filling every free slot. For a busy week, keep a short review and reduce the number of new goals.",
      ],
    },
    transfer:
      "Replace science with geography vocabulary or history source questions while retaining the same attempt–check–revisit structure. The task changes with the subject; the planning loop can remain familiar.",
    pitfalls: [
      "Do not assign identical review frequency to every topic by default. Use difficulty, deadlines and demonstrated understanding.",
      "School Calendar holds the plan; it does not assess mastery or automatically run a flashcard algorithm. Update the schedule after reviewing your work.",
    ],
    week: [
      { day: "Monday", task: "Science recall and feedback", minutes: 20 },
      {
        day: "Tuesday",
        task: "Maths independent equations and checking",
        minutes: 25,
      },
      {
        day: "Thursday",
        task: "Science delayed recall on difficult ideas",
        minutes: 15,
      },
      {
        day: "Friday",
        task: "English evidence paragraph and criteria check",
        minutes: 25,
      },
      {
        day: "Sunday",
        task: "Weekly study review and next actions",
        minutes: 15,
      },
    ],
  },
]

export const studySkills = [...principles, ...systems]
export const skillUrl = (skill: StudySkill) =>
  `/study-skills/${skill.kind.toLowerCase()}/${skill.slug}/`
export const getSkill = (id: string): StudySkill => {
  const skill = studySkills.find(item => item.id === id)
  if (!skill) throw new Error(`Unknown study skill: ${id}`)
  return skill
}
export const relatedSystems = (id: string) =>
  systems.filter(system =>
    [...system.principleIds, ...(system.optionalPrincipleIds || [])].includes(
      id
    )
  )
export const subjectSkillIds: { [subject: string]: string[] } = {
  English: ["cornell", "explanation"],
  Math: ["examples", "error-log"],
  Science: ["visuals", "feynman"],
  History: ["sq3r", "regulation"],
  Geography: ["visuals", "spacing"],
}
export const subjectUrl = (subject: string) =>
  `/activity-routines/study/${subject.toLowerCase()}-study-routines/`
export const studyArtwork: {
  [id: string]: { image: string; alt: string }
} = [
  [
    "overview",
    "An open book with a check mark representing practical learning skills",
  ],
  [
    "retrieval",
    "Question cards with a return arrow for recalling and checking an answer",
  ],
  ["spacing", "A calendar with review sessions spread across different days"],
  [
    "explanation",
    "Connected speech bubbles for explaining and checking an idea",
  ],
  [
    "interleaving",
    "Different shapes and colours mixed across two practice sets",
  ],
  [
    "examples",
    "A completed solution beside a partially completed practice problem",
  ],
  ["visuals", "A labelled diagram beside written explanations"],
  ["feedback", "A marked response with a correction check"],
  ["regulation", "A circular planning, monitoring and evaluation loop"],
  ["leitner", "Three boxes of flashcards for spaced review"],
  ["cornell", "A notebook page divided into cues, notes and a summary"],
  ["sq3r", "An open book with a magnifying glass and a question"],
  [
    "feynman",
    "A light bulb with a check mark for explaining and checking understanding",
  ],
  ["pomodoro", "A study timer for focused work and planned breaks"],
  [
    "example-cycle",
    "A worked solution and a practice task connected by repeat arrows",
  ],
  [
    "error-log",
    "A review clipboard with recorded mistakes and a correction check",
  ],
  [
    "weekly-review",
    "A weekly calendar with completed tasks and a review arrow",
  ],
].reduce<{ [id: string]: { image: string; alt: string } }>(
  (artwork, [id, alt]) => {
    artwork[id] = { image: `study-skill-${id}.png`, alt }
    return artwork
  },
  {}
)

export const studyGuideEntries = [
  {
    ...studyOverview,
    image: studyArtwork.overview.image,
    group: "Study skills",
    category: "Overview",
    activityType: "Study overview",
  },
  ...studySkills.map(skill => ({
    title: skill.title,
    description: skill.description,
    url: skillUrl(skill),
    image: studyArtwork[skill.id].image,
    group: "Study skills",
    category: skill.kind,
    activityType: "Study skills",
  })),
]
